import { ACCOUNT_SUSPENDED_MESSAGE, ErrorCode, normalizeErrorCode } from '../models/error.model';

/**
 * Shown when no more specific message is available.
 *
 * Single source of truth — this string was previously hardcoded in eight
 * separate files, which made it impossible to tell an intentional generic
 * fallback from a swallowed business error.
 */
export const GENERIC_ERROR_MESSAGE = 'Une erreur est survenue. Veuillez réessayer.';

/**
 * Shown when the stored session is gone (`Storage.getUserData()` returns null)
 * or the backend rejects the session with code "451".
 *
 * Deliberately actionable: the generic message would send the user into a retry
 * loop that can never succeed, because no amount of retrying restores a missing
 * session — only re-authenticating does.
 */
export const SESSION_EXPIRED_MESSAGE =
  'Votre session a expiré. Déconnectez-vous puis reconnectez-vous pour continuer.';

/**
 * Shown when a request exceeds its client-side time budget (see
 * `core/interceptors/timeout.interceptor.ts`).
 *
 * Not a business code: no `error_code` reaches us because no response reaches
 * us at all. The interceptor injects this copy as the body of a synthetic
 * `HttpErrorResponse` so it flows through `extractErrorMessage` like any
 * server-provided message.
 *
 * Deliberately invites a retry, unlike SESSION_EXPIRED_MESSAGE: a timeout is a
 * transient condition, and retrying is exactly the action that can succeed.
 *
 * That invitation is only safe because the Gozem Money **execute** step is
 * excluded from the timeout entirely (`noTimeoutContext` in
 * `core/http/request-timeout.ts`). Those calls move money and carry no
 * idempotency key, so a request we abandoned may have succeeded server-side —
 * telling the user to "réessayer" there would invite a duplicate transfer. If a
 * money-moving endpoint is ever brought under the timeout, it needs its own copy
 * that points at the transaction history instead of prompting a retry.
 */
export const REQUEST_TIMEOUT_MESSAGE =
  'Le serveur met trop de temps à répondre. Vérifiez votre connexion et réessayez.';

/**
 * Business error code → user-facing French copy.
 *
 * ## Why this exists
 *
 * The API answers in English only. Before this registry existed, a coded
 * business error reached the user in one of two bad shapes: raw English
 * ("Gozem money user not found"), or — when the backend sent the error with a
 * 5xx status — the generic "Une erreur est survenue" fallback, because
 * `extractErrorMessage` suppresses 5xx bodies to avoid leaking stack traces.
 * Code "1032" hit exactly that path and shipped to staging as a dead-end
 * message inviting a retry that could never succeed.
 *
 * ## The rule
 *
 * A business error is identified by its **code**, never by its message text.
 * Message text is server-controlled, locale-dependent and unversioned; the code
 * is the contract. Anything that branches on wording is a latent bug.
 *
 * ## Adding a code
 *
 * Add the member to {@link ErrorCode} *and* the copy below. The map is typed
 * `Record<ErrorCode, string>`, so the compiler rejects a member without copy.
 */
const BUSINESS_ERROR_MESSAGES: Readonly<Record<ErrorCode, string>> = {
  [ErrorCode.MERCHANT_NOT_FOUND]: 'Aucun compte marchand ne correspond à cet identifiant.',
  [ErrorCode.INVALID_PASSWORD]: 'Mot de passe incorrect.',
  [ErrorCode.INVALID_OTP]: 'Code de vérification invalide. Demandez un nouveau code.',
  [ErrorCode.ACCOUNT_SUSPENDED]: ACCOUNT_SUSPENDED_MESSAGE,
  [ErrorCode.SESSION_EXPIRED]: SESSION_EXPIRED_MESSAGE,
  // Deliberately does NOT invite a retry: no amount of retrying creates a wallet
  // for a beneficiary who has none. Same reasoning as SESSION_EXPIRED_MESSAGE.
  [ErrorCode.GOZEM_MONEY_USER_NOT_FOUND]:
    "Ce bénéficiaire n'a pas de compte Gozem Money. Vérifiez le numéro ou choisissez un autre bénéficiaire.",
};

/**
 * The mapped copy for `code`, or `null` when we have none.
 *
 * `Object.hasOwn`, not the `in` operator: `in` walks the prototype chain, so a code
 * of "toString" or "constructor" would report as mapped and then hand back a function
 * where a message was expected.
 */
function mappedMessage(code: unknown): string | null {
  const normalized = normalizeErrorCode(code);
  if (normalized === null || !Object.hasOwn(BUSINESS_ERROR_MESSAGES, normalized)) {
    return null;
  }
  return BUSINESS_ERROR_MESSAGES[normalized as ErrorCode];
}

/**
 * True when `code` is a business code we have French copy for.
 *
 * Used by the HTTP interceptor to report *unmapped* coded errors to Sentry, so
 * a new backend code surfaces as an alert instead of being discovered by manual
 * QA the way "1032" was.
 */
export function isMappedBusinessErrorCode(code: unknown): boolean {
  return mappedMessage(code) !== null;
}

/**
 * Resolve a backend error code to user-facing French copy.
 *
 * Accepts `unknown` because the payment/auth APIs are inconsistent about
 * sending the code as a JSON string or number — {@link normalizeErrorCode}
 * settles that.
 *
 * @param fallback Used when the code is absent or unmapped. Callers pass the
 *   server's own message here, so an unmapped code still shows something
 *   specific rather than degrading straight to the generic text.
 */
export function resolveBusinessErrorMessage(code: unknown, fallback: string): string {
  return mappedMessage(code) ?? fallback;
}
